import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Fade,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/uz';
import { fetchClients, fetchMails, fetchProducts, fetchData } from '../api/github';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const COLORS = ['#007bff', '#28a745', '#ff9900'];
const DEFAULT_IMAGE = 'https://via.placeholder.com/40?text=No+Image';

const formatMoney = (value) => {
  if (!value) return '0';
  const num = value.toString().replace(/\D/g, '');
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' Сум';
};

const getTheme = (mode) => createTheme({
  palette: {
    mode: mode,
    primary: { main: '#007bff' },
    secondary: { main: '#28a745' },
    background: {
      default: mode === 'light' ? '#f5f7fa' : '#1e1e1e',
      paper: mode === 'light' ? '#ffffff' : '#2c2c2c',
    },
    text: {
      primary: mode === 'light' ? '#333' : '#fff',
      secondary: mode === 'light' ? '#666' : '#bbb',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h4: { fontWeight: 700, fontSize: '1.5rem', '@media (max-width: 600px)': { fontSize: '1.2rem' } },
    h6: { fontWeight: 600, fontSize: '1rem', '@media (max-width: 600px)': { fontSize: '0.9rem' } },
    body2: { fontSize: '0.875rem', '@media (max-width: 600px)': { fontSize: '0.75rem' } },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          transition: 'transform 0.3s ease-in-out',
          '&:hover': { transform: 'translateY(-2px)' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'capitalize',
          padding: '8px 16px',
          '@media (max-width: 600px)': { padding: '6px 12px' },
          backgroundColor: '#007bff',
          color: '#fff',
          '&:hover': { backgroundColor: '#0056b3' },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: '8px', '& .MuiOutlinedInput-root': { borderRadius: '8px' } },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          marginBottom: '8px',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

const Analytics = ({ isMobile }) => {
  const systemTheme = useTheme();
  const isMobileDevice = useMediaQuery(systemTheme.breakpoints.down('sm')) || isMobile;
  const [themeMode, setThemeMode] = useState(localStorage.getItem('themeMode') || 'light');
  const theme = getTheme(themeMode);
  const [clients, setClients] = useState([]);
  const [mails, setMails] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('month');
  const [metricFilter, setMetricFilter] = useState('orderAmount');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clientsData, mailsData, productsData, employeesData] = await Promise.all([
        fetchClients(),
        fetchMails(),
        fetchProducts(),
        fetchData('employees.json'),
      ]);
      setClients(clientsData || []);
      setMails(mailsData || []);
      setProducts(productsData || []);
      setEmployees(employeesData || []);
    } catch (err) {
      console.error('Хатолик юз берди:', err);
      setError('Маълумотларни юклашда хатолик юз берди');
    } finally {
      setLoading(false);
    }
  };  

  useEffect(() => {
    moment.locale('uz');
    loadData();
  }, []);

  const filterByTime = (item, dateField) => {
    const itemDate = moment(item[dateField]);
    const now = moment();
    if (timeFilter === 'day') {
      return itemDate.isSame(now, 'day');
    } else if (timeFilter === 'month') {
      return itemDate.isSame(now, 'month');
    } else if (timeFilter === 'year') {
      return itemDate.isSame(now, 'year');
    }
    return true;
  };

  const calculateWorkHours = (employee, filteredAttendance) => {
    const workDays = filteredAttendance.filter(record => record.status === 'work');
    let totalHours = 0;
    workDays.forEach(record => {
      const dailyHours = employee.schedule?.dailyHours;
      if (dailyHours) {
        const start = moment(dailyHours.start, 'HH:mm');
        const end = moment(dailyHours.end, 'HH:mm');
        const breakStart = moment(dailyHours.breakStart, 'HH:mm');
        const breakEnd = moment(dailyHours.breakEnd, 'HH:mm');
        const workHours = end.diff(start, 'hours') - (breakEnd.diff(breakStart, 'hours'));
        totalHours += workHours > 0 ? workHours : 0;
      }
    });
    return totalHours;
  };

  const processedData = useMemo(() => {
    const filteredClients = clients.filter(client =>
      client.orders.some(order => filterByTime(order, 'date'))
    );
    const filteredMails = mails.filter(mail => filterByTime(mail, 'createdAt'));

    const totalClients = filteredClients.length;
    const totalMails = filteredMails.length;
    const totalClientOrdersAmount = filteredClients.reduce((sum, client) =>
      sum + client.orders
        .filter(order => filterByTime(order, 'date'))
        .reduce((orderSum, order) => orderSum + order.totalAmount, 0), 0);
    const totalMailOrdersAmount = filteredMails.reduce((sum, mail) => sum + mail.amount, 0);
    const totalOrdersAmount = totalClientOrdersAmount + totalMailOrdersAmount;

    const clientPaidAmount = filteredClients.reduce((sum, client) =>
      sum + client.orders
        .filter(order => filterByTime(order, 'date'))
        .reduce((orderSum, order) => orderSum + (order.paymentInfo?.paidAmount || 0), 0), 0);
    const mailPaidAmount = filteredMails.reduce((sum, mail) => sum + (mail.paidAmount || 0), 0);
    const totalPaidAmount = clientPaidAmount + mailPaidAmount;
    const totalUnpaidAmount = totalPaidAmount > totalOrdersAmount ? 0 : totalOrdersAmount - totalPaidAmount;
    const totalPartiallyPaid = filteredClients.reduce((sum, client) =>
      sum + client.orders
        .filter(order => filterByTime(order, 'date') && order.paymentInfo?.paidAmount > 0 && order.paymentInfo?.paidAmount < order.totalAmount)
        .reduce((orderSum, order) => orderSum + order.paymentInfo.paidAmount, 0), 0) +
      filteredMails.filter(mail => mail.paidAmount > 0 && mail.paidAmount < mail.amount)
        .reduce((sum, mail) => sum + mail.paidAmount, 0);

    const totalProductsSold = filteredClients.reduce((sum, client) =>
      sum + client.orders
        .filter(order => filterByTime(order, 'date'))
        .reduce((orderSum, order) =>
          orderSum + order.products.reduce((prodSum, prod) => prodSum + (+prod.quantity || 0), 0), 0), 0) +
      filteredMails.reduce((sum, mail) =>
        sum + mail.products.reduce((prodSum, prod) => prodSum + (+prod.quantity || 0), 0), 0);

    const employeeStats = {};
    

    employees.forEach(employee => {
      const empId = employee.id || 'unknown';
      const empName = employee.username || 'Номаълум';
      const filteredAttendance = (employee.attendance || []).filter(record =>
        filterByTime({ date: record.date }, 'date')
      );
      employeeStats[empId] = {
        name: empName,
        clientsCount: 0,
        mailsCount: 0,
        clientsAmount: 0,
        mailsAmount: 0,
        paidAmount: 0,
        unpaidAmount: 0,
        partiallyPaidAmount: 0,
        overpaidAmount: 0,
        productsSold: 0,
        workDays: filteredAttendance.filter(record => record.status === 'work').length,
        absentDays: filteredAttendance.filter(record => record.status === 'absent').length,
        workHours: calculateWorkHours(employee, filteredAttendance),
        productsDetails: [],
      };
    });


    filteredClients.forEach(client => {
      client.orders
        .filter(order => filterByTime(order, 'date'))
        .forEach(order => {
          const empId = order.employeeId || 'unknown';
          if (!employeeStats[empId]) return;
          employeeStats[empId].clientsCount += 1;
          employeeStats[empId].clientsAmount += order.totalAmount;
          const paid = order.paymentInfo?.paidAmount || 0;
          employeeStats[empId].paidAmount += paid;
          employeeStats[empId].unpaidAmount += (order.totalAmount - paid) > 0 ? order.totalAmount - paid : 0;
          if (paid > 0 && paid < order.totalAmount) {
            employeeStats[empId].partiallyPaidAmount += paid;
          }
          if (paid > order.totalAmount) {
            employeeStats[empId].overpaidAmount += paid - order.totalAmount;
          }
          order.products.forEach(prod => {
            const productInfo = products.find(p => p.id === prod.productId) || {};
            const productName = prod.productName || productInfo.name || `Номаълум махсулот (${prod.productId || 'no_id'})`;
            const imageUrl = productInfo.image || DEFAULT_IMAGE;
            if (!prod.productName && !productInfo.name) {
              console.warn(`Номсиз махсулот:`, { productId: prod.productId, quantity: prod.quantity });
            }
            const existingProduct = employeeStats[empId].productsDetails.find(p => p.id === prod.productId);
            if (existingProduct) {
              existingProduct.quantity += (+prod.quantity || 0);
            } else {
              employeeStats[empId].productsDetails.push({
                id: prod.productId,
                name: productName,
                quantity: (+prod.quantity || 0),
                imageUrl,
              });
            }
          });
          employeeStats[empId].productsSold += order.products.reduce((sum, prod) => sum + (+prod.quantity || 0), 0);
        });
    });

    filteredMails.forEach(mail => {
      const empId = mail.employeeId || 'unknown';
      if (!employeeStats[empId]) return;
      employeeStats[empId].mailsCount += 1;
      employeeStats[empId].mailsAmount += mail.amount;
      const paid = mail.paidAmount || 0;
      employeeStats[empId].paidAmount += paid;
      employeeStats[empId].unpaidAmount += (mail.amount - paid) > 0 ? mail.amount - paid : 0;
      if (paid > 0 && paid < mail.amount) {
        employeeStats[empId].partiallyPaidAmount += paid;
      }
      if (paid > mail.amount) {
        employeeStats[empId].overpaidAmount += paid - mail.amount;
      }
      mail.products.forEach(prod => {
        const productInfo = products.find(p => p.id === prod.productId) || {};
        const productName = prod.productName || productInfo.name || `Номаълум махсулот (${prod.productId || 'no_id'})`;
        const imageUrl = productInfo.image || DEFAULT_IMAGE;
        if (!prod.productName && !productInfo.name) {
          console.warn(`Номсиз махсулот:`, { productId: prod.productId, quantity: prod.quantity });
        }
        const existingProduct = employeeStats[empId].productsDetails.find(p => p.id === prod.productId);
        if (existingProduct) {
          existingProduct.quantity += (+prod.quantity || 0);
        } else {
          employeeStats[empId].productsDetails.push({
            id: prod.productId,
            name: productName,
            quantity: (+prod.quantity || 0),
            imageUrl,
          });
        }
      });
      employeeStats[empId].productsSold += mail.products.reduce((sum, prod) => sum + (+prod.quantity || 0), 0);
    });

    // Сортировка productsDetails по количеству
    Object.values(employeeStats).forEach(emp => {
      emp.productsDetails.sort((a, b) => b.quantity - a.quantity);
    });

    return {
      totalClients,
      totalMails,
      totalOrdersAmount,
      totalPaidAmount,
      totalUnpaidAmount,
      totalPartiallyPaid,
      totalProductsSold,
      employeeStats: Object.values(employeeStats),
    };
  }, [clients, mails, employees, products, timeFilter]);

  const {
    totalClients,
    totalMails,
    totalOrdersAmount,
    totalPaidAmount,
    totalUnpaidAmount,
    totalPartiallyPaid,
    totalProductsSold,
    employeeStats,
  } = processedData;

  const sortedEmployeeStats = [...employeeStats].sort((a, b) => {
    if (metricFilter === 'orderAmount') return (b.clientsAmount + b.mailsAmount) - (a.clientsAmount + a.mailsAmount);
    if (metricFilter === 'clientsCount') return b.clientsCount - a.clientsCount;
    if (metricFilter === 'mailsCount') return b.mailsCount - a.mailsCount;
    if (metricFilter === 'productsSold') return b.productsSold - a.productsSold;
    if (metricFilter === 'workDays') return b.workDays - a.workDays;
    if (metricFilter === 'workHours') return b.workHours - a.workHours;
    return 0;
  });

  const topEmployees = sortedEmployeeStats.slice(0, 3);
 const exportToCSV = () => {
    const headers = [
      'Ходим',
      'Мижозлар сони',
      'Почталар сони',
      'Мижозлар суммаси',
      'Почталар суммаси',
      'Тўланган сумма',
      'Тўланмаган сумма',
      'Қисман тўланган сумма',
      'Переплаченная сумма',
      'Сотилган махсулотлар',
      'Проданные товары',
      'Ишлаган кунлар',
      'Ўтказиб юборилган кунлар',
      'Ишлаган соат',
    ];
    const rows = sortedEmployeeStats.map(emp => [
      `"${emp.name}"`, // Оборачиваем в кавычки для корректной обработки
      emp.clientsCount,
      emp.mailsCount,
      `"${formatMoney(emp.clientsAmount)}"`,
      `"${formatMoney(emp.mailsAmount)}"`,
      `"${formatMoney(emp.paidAmount)}"`,
      `"${formatMoney(emp.unpaidAmount)}"`,
      `"${formatMoney(emp.partiallyPaidAmount)}"`,
      `"${formatMoney(emp.overpaidAmount)}"`,
      emp.productsSold,
      `"${emp.productsDetails.map(prod => `${prod.name}: ${prod.quantity} шт.`).join(';') || 'Йўқ'}"`, // Используем | вместо ;
      emp.workDays,
      emp.absentDays,
      emp.workHours,
    ]);
    const csvContent = [
      headers.join(';'), // Используем ; как разделитель
      ...rows.map(row => row.join(';')),
    ].join('\n');
    // Добавляем BOM для UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Analitika_${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Fade in={true} timeout={500}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        </Fade>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Fade in={true} timeout={500}>
          <Box sx={{ p: isMobileDevice ? 2 : 4 }}>
            <Typography color="error">{error}</Typography>
            <Button
              onClick={loadData}
              variant="contained"
              color="primary"
              sx={{ mt: 2, px: isMobileDevice ? 3 : 4 }}
            >
              Қайта юклаш
            </Button>
          </Box>
        </Fade>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Fade in={true} timeout={500}>
        <Box sx={{ p: isMobileDevice ? 2 : 4, bgcolor: 'background.default' }}>
          {/* Фильтры */}
          <Grid container spacing={2} sx={{ mb: 3, flexDirection: isMobileDevice ? 'column' : 'row' }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size={isMobileDevice ? 'small' : 'medium'} variant="outlined">
                <InputLabel>Вақт фильтри</InputLabel>
                <Select
                  value={timeFilter}
                  onChange={e => setTimeFilter(e.target.value)}
                  label="Вақт фильтри"
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="day">Кунлик</MenuItem>
                  <MenuItem value="month">Ойлик</MenuItem>
                  <MenuItem value="year">Йиллик</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size={isMobileDevice ? 'small' : 'medium'} variant="outlined">
                <InputLabel>Метрика</InputLabel>
                <Select
                  value={metricFilter}
                  onChange={e => setMetricFilter(e.target.value)}
                  label="Метрика"
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="orderAmount">Буйуртма суммаси</MenuItem>
                  <MenuItem value="clientsCount">Мижозлар сони</MenuItem>
                  <MenuItem value="mailsCount">Почталар сони</MenuItem>
                  <MenuItem value="productsSold">Сотилган махсулотлар</MenuItem>
                  <MenuItem value="workDays">Рабочие дни</MenuItem>
                  <MenuItem value="workHours">Рабочие часы</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Лучшие сотрудники */}
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Енг яхши ходимлар
          </Typography>
          <Grid sx={{ mb: 4 }}>
            {topEmployees.map((emp, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Card sx={{ bgcolor: COLORS[index % COLORS.length], color: '#fff', borderRadius: '12px' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      #{index + 1} {emp.name}
                    </Typography>
                    <Typography variant="h6">
                      Сумма: {formatMoney(emp.paidAmount)}
                    </Typography>
                    <Typography variant="h6">
                      Мижозлар: {emp.clientsCount}
                    </Typography>
                    <Typography variant="h6">
                      Почталар: {emp.mailsCount}
                    </Typography>
                    <Typography variant="h6">
                      Ишлаган кунлар: {emp.workDays}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Общая статистика */}
          <Grid container spacing={2} sx={{ mb: 4, justifyContent: 'space-around' }}>
            {[
              { label: 'Мижозлар сони', value: totalClients, color: 'primary.main' },
              { label: 'Почталар сони', value: totalMails, color: 'secondary.main' },
              { label: 'Буйуртма суммаси', value: formatMoney(totalOrdersAmount), color: 'primary.main' },
              { label: 'Тўланган сумма', value: formatMoney(totalPaidAmount), color: 'secondary.main' },
              { label: 'Тўланмаган сумма', value: formatMoney(totalUnpaidAmount), color: 'error.main' },
              { label: 'Қисман тўланган', value: formatMoney(totalPartiallyPaid), color: 'warning.main' },
              { label: 'Сотилган махсулотлар', value: totalProductsSold, color: 'primary.main' },
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  sx={{
                    fontSize: '12px',
                    textAlign: 'center',
                    p: 1,
                    transform: 'scale(0.9)',
                    bgcolor: 'background.paper',
                    transition: 'transform 0.3s',
                    gap: 1,
                    '&:hover': { transform: 'scale(1.02)' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {stat.label}
                  </Typography>
                  <Typography variant="h6" sx={{ color: stat.color, fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Статистика по сотрудникам */}
          <Paper sx={{ p: isMobileDevice ? 1.5 : 2, mb: 4, bgcolor: 'background.paper' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Ходимлар бўйича статистика
            </Typography>
            {sortedEmployeeStats.map((emp, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flexGrow: 1 }}>{emp.name}</Typography>
                  <Chip
                    label={formatMoney(emp.paidAmount)}
                    color="primary"
                    size="small"
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Мижозлар сони:</strong> {emp.clientsCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Почталар сони:</strong> {emp.mailsCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Мижозлар суммаси:</strong> {formatMoney(emp.clientsAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Почталар суммаси:</strong> {formatMoney(emp.mailsAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Тўланган сумма:</strong> {formatMoney(emp.paidAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Тўланмаган сумма:</strong> {formatMoney(emp.unpaidAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Қисман тўланган сумма:</strong> {formatMoney(emp.partiallyPaidAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Ортиқча тўланган:</strong> {formatMoney(emp.overpaidAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Сотилган махсулотлар (жами):</strong> {emp.productsSold}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Ишлаган кунлар:</strong> {emp.workDays}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Ўтказиб юборилган кунлар:</strong> {emp.absentDays}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Ишлаган соати:</strong> {emp.workHours}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                        <strong>Сотилган махсулотлар рўйхати:</strong>
                      </Typography>
                      {emp.productsDetails.length > 0 ? (
                        <List dense>
                          {emp.productsDetails.map((prod, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemAvatar>
                                <Avatar
                                  src={prod.imageUrl}
                                  alt={prod.name}
                                  sx={{ width: 40, height: 40, mr: 2 }}
                                  variant="square"
                                />
                              </ListItemAvatar>
                              <ListItemText primary={`${prod.name}: ${prod.quantity} шт.`} />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ ml: 2 }}>
                          Сотилган махсулотлар йўқ
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
            <Button
              onClick={exportToCSV}
              variant="contained"
              color="primary"
              sx={{ mt: 2, px: isMobileDevice ? 3 : 4 }}
              disabled={sortedEmployeeStats.length === 0}
            >
              Жадвалга экспорт қилиш
            </Button>
          </Paper>
        </Box>
      </Fade>
    </ThemeProvider>
  );
};

export default Analytics;